// src/client/renderer/vfx/tile/approach-highlight-pool.ts
//
// Plan 16, Task 2.2 — Approach highlight pool (per-step preview during movement).
//
// Geometry: RingGeometry — a flat ring rendered on the tile surface.
// One shared geometry; per-emitter MeshBasicMaterial clone.
//
// Animation timeline (gsap, manual-ticker via GsapRegistry):
//   0.000–0.250s  scale 0.6 → 1.2  (power2.out)
//   0.250–0.450s  opacity 1 → 0    (power2.in)
// Total lifetime: ~0.450s. One-shot — isFinished returns true once timeline
// reaches position 0.45s (set via tl.call callback, NOT eventCallback).
//
// Forward-defense: pool.dispose() frees all GPU resources for active emitters
// WITHOUT relying on prior pool.release() calls. Safe to call from manager.dispose().

import * as THREE from 'three';
import type { TileVfxPool, TileVfxSpawnArgs } from './tile-vfx-types';
import type { GsapRegistry } from '../gsap-registry';
import type { GameEvent } from '../../../../shared/game/types';

// ─────────────────────────────────────────────────────────────────────────────
// Animation timing constants
// ─────────────────────────────────────────────────────────────────────────────

const SCALE_START = 0.6;
const SCALE_END = 1.2;
const SCALE_DURATION = 0.25; // seconds

const FADE_START_OPACITY = 1.0;
const FADE_END_OPACITY = 0.0;
const FADE_START_T = 0.25; // seconds — offset in timeline
const FADE_DURATION = 0.2; // seconds

/** Absolute position in timeline at which the emitter is considered finished. */
const FINISH_AT = 0.45; // seconds

// ─────────────────────────────────────────────────────────────────────────────
// Geometry constants (RingGeometry)
// ─────────────────────────────────────────────────────────────────────────────

const RING_INNER_RADIUS = 0.3;
const RING_OUTER_RADIUS = 0.5;
const RING_SEGMENTS = 32;

// ─────────────────────────────────────────────────────────────────────────────
// Internal emitter state
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterState {
  /** The gsap timeline handle managing this emitter's animation. */
  handle: ReturnType<GsapRegistry['create']>;
  /** Whether the tl.call callback has fired (isFinished guard). */
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export interface ApproachHighlightPoolConfig {
  readonly scene: THREE.Scene;
  readonly registry: GsapRegistry;
}

export function createApproachHighlightPool({
  scene,
  registry,
}: ApproachHighlightPoolConfig): TileVfxPool {
  // Shared geometry — disposed exactly once in pool.dispose()
  const sharedGeometry = new THREE.RingGeometry(
    RING_INNER_RADIUS,
    RING_OUTER_RADIUS,
    RING_SEGMENTS,
  );

  // Per-emitter state map: emitter → { handle, finished }
  const emitterStates = new Map<THREE.Object3D, EmitterState>();

  let disposed = false;

  // ── acquire ────────────────────────────────────────────────────────────────

  function acquire(args: TileVfxSpawnArgs): THREE.Object3D | null {
    // Per-emitter material clone (transparent, additive-style)
    const material = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: FADE_START_OPACITY,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    const mesh = new THREE.Mesh(sharedGeometry, material);
    mesh.position.set(args.position.x, args.position.y, args.position.z);

    // Rotate flat (XZ plane — ring lies on the tile surface)
    mesh.rotation.x = -Math.PI / 2;

    // Start at SCALE_START
    mesh.scale.set(SCALE_START, SCALE_START, SCALE_START);

    // Build gsap timeline via registry
    const handle = registry.create('approach_highlight');
    const tl = handle.timeline;

    const state: EmitterState = { handle, finished: false };

    // Tween 1: scale 0.6 → 1.2 over 250ms (starts at t=0)
    tl.to(
      mesh.scale,
      {
        x: SCALE_END,
        y: SCALE_END,
        z: SCALE_END,
        duration: SCALE_DURATION,
        ease: 'power2.out',
      },
      0,
    );

    // Tween 2: opacity 1 → 0 over 200ms (starts at t=0.25)
    // Animate a proxy; onUpdate applies to the material to stay Three.js-friendly.
    const opacityProxy = { value: FADE_START_OPACITY };
    tl.to(
      opacityProxy,
      {
        value: FADE_END_OPACITY,
        duration: FADE_DURATION,
        ease: 'power2.in',
        onUpdate: () => {
          material.opacity = opacityProxy.value;
        },
      },
      FADE_START_T,
    );

    // Completion callback — use tl.call(fn, undefined, absolutePos) per spec.
    // Do NOT use eventCallback('onComplete') — see gsap-registry.ts VIRTUAL_DURATION note.
    tl.call(
      () => {
        state.finished = true;
      },
      undefined,
      FINISH_AT,
    );

    emitterStates.set(mesh, state);
    return mesh;
  }

  // ── update ─────────────────────────────────────────────────────────────────
  // The registry's update(dt) drives ALL timelines. This method just returns
  // the current active set. Caller (manager) advances dt via registry.update().

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    return new Set(emitterStates.keys());
  }

  // ── isFinished ─────────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    const state = emitterStates.get(emitter);
    if (!state) return true; // not tracked — treat as finished
    return state.finished;
  }

  // ── release ────────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    const state = emitterStates.get(emitter);
    if (!state) return; // safe no-op

    state.handle.dispose(); // kills timeline, removes from registry

    emitterStates.delete(emitter);

    // Remove from scene if attached
    scene.remove(emitter);

    // Dispose per-emitter material
    const mesh = emitter as THREE.Mesh;
    if (mesh.material instanceof THREE.Material) {
      mesh.material.dispose();
    }
  }

  // ── dispose ────────────────────────────────────────────────────────────────
  // Forward-defense: iterates active emitters and frees per-emitter resources
  // WITHOUT expecting prior release() calls.

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    for (const [emitter, state] of emitterStates) {
      state.handle.dispose(); // kill timeline

      scene.remove(emitter);

      // Dispose per-emitter material
      const mesh = emitter as THREE.Mesh;
      if (mesh.material instanceof THREE.Material) {
        mesh.material.dispose();
      }
    }
    emitterStates.clear();

    // Dispose shared geometry exactly once
    sharedGeometry.dispose();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps a `MovementStepEvent` to approach highlight spawn args.
 *
 * Returns null for:
 *   - any non-movement_step event
 *   - movement_step when `getPositionForNode` is not provided
 *   - movement_step when `getPositionForNode(event.toNodeId)` returns undefined
 *
 * Position lookup for node→world-pos is owned by Plan 19; for Plan 16 the
 * resolver is a pass-through that delegates the lookup to the supplied callback.
 */
export function resolveApproachHighlightForEvent(
  event: GameEvent,
  getPositionForNode?: (nodeId: string) => { x: number; y: number; z: number } | undefined,
): { poolId: 'approach_highlight'; args: TileVfxSpawnArgs } | null {
  if (event.type !== 'movement_step') return null;

  if (!getPositionForNode) return null;

  const position = getPositionForNode(event.toNodeId);
  if (!position) return null;

  return {
    poolId: 'approach_highlight',
    args: { position },
  };
}
